// UserDrawer.tsx · v12.10.30 (#2234 the long sign-in block, last on Profile) · 2026-11-03 · VoiceSpectrum AI
// UserDrawer.tsx · v12.10.26 (#2135 Mobile = the standard phone input; #2133 Copy invite link beside Resend invite) · 2026-10-30 · VoiceSpectrum AI
// VoiceSpectrum.Web.T2 · v12.10.9.1 (live pass: a person with no name on file no longer blocks group changes) · v12.10.9 (Security Phase 1 – #1258/#1259/#1260/#1261) · 2026-10-06
// UserDrawer.tsx - the person drawer opened from Users (prototype scPaintDrawer, admin mode). Tabs Profile · Access (§15;
// Activity is hidden until the audit viewer ships). One Save for the whole drawer; unsaved edits show on the tab label (§31).
//   Profile: name (not for invites - they fill it in themselves, S28), job title, email / mobile; sign-in & 2-step
//            (Send password reset link, Reset 2-step); Sign out everywhere.
//   Access:  groups (multi-group picker, no escalation §36, last-manager lock S14), invitation (Resend), and the
//            danger zone: Disable -> Enable / Delete (typed name, only from Disabled - S37), Cancel invite.
// A person's own row opens Your account instead (nobody changes their own groups).
import { useMemo, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { isConfirmCancelled, withConfirm } from '../../components/security/ConfirmItsYou';
import {
  Avatar, GroupPicker, GroupPill, StatusTags, displayName, enforceShort, groupsLabel, isInvite, isLastManager, isLocked, lockMinsLeft, requiredFor,
} from '../../components/security/people';
import { ConfirmDialog, Ic, InlineError, digits, fmtClock, fmtDate, lastFour, plural, useEscClose, type ConfirmOpts } from '../../components/security/ui';
import { securityApi, securityErrorMessage, type Group, type Policy, type UserRow } from '../../lib/securityApi';
import { useAccess } from '../../lib/useAccess';
import { isForeign, maskPhone, ONLY_US_CANADA, phoneFromInput, toE164 } from '../../lib/phone';   // v12.10.26 (#2135)
import { toast } from '../../lib/toast';
import type { UserActions } from './userActions';
import { LongSignInBlock } from '../../components/security/LongSignInBlock';   // v12.10.30 (#2234)

interface Draft { firstName: string; lastName: string; jobTitle: string; email: string; mobile: string; groups: number[] }
const draftOf = (u: UserRow): Draft => ({ firstName: u.firstName ?? '', lastName: u.lastName ?? '', jobTitle: u.jobTitle ?? '', email: u.email ?? '', mobile: u.mobile ?? '', groups: u.groups.slice() });
const PROFILE_KEYS: (keyof Draft)[] = ['firstName', 'lastName', 'jobTitle', 'email', 'mobile'];
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const mobileOk = (s: string) => { const d = digits(s); return d.length === 10 || (d.length === 11 && d.startsWith('1')); };
const norm10 = (s: string) => digits(s).slice(-10);

export default function UserDrawer({ user, users, groups, policy, actions, onClose, onSaved, backTo }: {
  user: UserRow; users: UserRow[]; groups: Group[]; policy: Policy | null; actions: UserActions;
  onClose: () => void; onSaved: (row: UserRow | null) => void; backTo?: { label: string; onBack: () => void };
}) {
  const { hasAccess } = useAccess();
  const [tab, setTab] = useState<'profile' | 'access'>('profile');
  const [draft, setDraft] = useState<Draft>(() => draftOf(user));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [confirm, setConfirm] = useState<ConfirmOpts | null>(null);
  const now = Date.now();

  const base = useMemo(() => draftOf(user), [user]);
  const profileDirty = PROFILE_KEYS.some((k) => draft[k] !== base[k]);
  const accessDirty = JSON.stringify(draft.groups) !== JSON.stringify(base.groups);
  const dirty = profileDirty || accessDirty;

  const invite = isInvite(user);
  const canEdit = hasAccess('sec_users', 'edit');
  const canGroups = hasAccess('sec_users', 'roles') && !user.self;
  const lastMgr = isLastManager(user, users, groups);
  const name = displayName(user);

  const errs: Partial<Record<keyof Draft | 'ident', string>> = {};
  // v12.10.9 live pass: a person who never gave a name (they complete it at their next sign-in, L13) must not block an
  // administrator from changing their groups or disabling them - the name is required only once one is on file or typed.
  const hadName = !!(base.firstName.trim() || base.lastName.trim());
  const typingName = !!(draft.firstName.trim() || draft.lastName.trim());
  if (!invite && (hadName || typingName)) {
    if (!draft.firstName.trim()) errs.firstName = 'First name is needed.';
    if (!draft.lastName.trim()) errs.lastName = 'Last name is needed.';
  }
  const em = draft.email.trim(), ph = draft.mobile.trim();
  if (!em && !ph) errs.ident = 'Add an email or a mobile number - at least one is needed to sign in.';
  if (em && !EMAIL_RE.test(em)) errs.email = 'That doesn’t look like an email address.';
  else if (em && users.some((u) => u.id !== user.id && (u.email ?? '').toLowerCase() === em.toLowerCase())) errs.email = 'Someone else here already uses this email.';
  // v12.10.26 (#2135): the app's phone rules - "+1 (908) 555-0142"; a foreign number says so; checked again on every change.
  if (ph && isForeign(ph)) errs.mobile = ONLY_US_CANADA;
  else if (ph && !(toE164(ph) || mobileOk(ph))) errs.mobile = 'Enter a 10-digit mobile number.';
  else if (ph && users.some((u) => u.id !== user.id && u.mobile && norm10(u.mobile) === norm10(ph))) errs.mobile = 'Someone else here already uses this number.';
  if (!draft.groups.length) errs.groups = 'Pick at least one group.';
  const valid = Object.keys(errs).length === 0;
  const canSave = dirty && valid && (!profileDirty || canEdit) && (!accessDirty || canGroups);

  function requestClose() {
    if (!dirty) { onClose(); return; }
    setConfirm({ sev: 'warn', title: 'Discard your changes?', body: 'You changed this person but haven’t saved it.', label: 'Discard changes', danger: true, cancelLabel: 'Keep editing', onOk: () => onClose() });
  }
  useEscClose(() => { if (!confirm) requestClose(); }, true);

  async function save() {
    if (!canSave || saving) return;
    setSaving(true); setErr('');
    let row: UserRow | null = null;
    try {
      if (profileDirty) {
        const identChanged = em !== (user.email ?? '') || ph !== (user.mobile ?? '');
        const body = {
          firstName: invite ? (user.firstName ?? '') : draft.firstName.trim(),
          lastName: invite ? (user.lastName ?? '') : draft.lastName.trim(),
          email: em || null, mobile: ph || null, jobTitle: draft.jobTitle.trim() || null,
        };
        row = await withConfirm(() => securityApi.updateUser(user.id, body), identChanged ? `You’re changing ${name}’s sign-in email or phone.` : undefined);
      }
      if (accessDirty) {
        row = await withConfirm(() => securityApi.setUserGroups(user.id, draft.groups), `You’re changing ${name}’s groups.`);
      }
      onSaved(row);
      toast('success', `Saved ${name}`);
      onClose();
    } catch (e) {
      if (isConfirmCancelled(e)) return;
      if (row) onSaved(row);   // the profile part landed; the groups part did not
      setErr(securityErrorMessage(e, 'Your changes weren’t saved. Try again.'));
    } finally { setSaving(false); }
  }

  const m = user.methods ?? [];
  const locked = isLocked(user, now);
  const lockBanner = locked && (
    <div className="sc-warn bad"><Ic k="lock" /><div>
      <b>Locked</b> after {policy?.lockAttempts ?? 5} wrong tries. Unlocks by itself at {fmtClock(user.lockedUntil)} ({plural(lockMinsLeft(user, now), 'min')} from now).{' '}
      {hasAccess('sec_users', 'unlock') && <button type="button" className="sc-link" onClick={() => void actions.unlock(user)}>Unlock now</button>}
    </div></div>
  );

  const idStatus = (k: 'email' | 'mobile') => {
    if (invite) return <span className="sc-idst">Confirmed when they accept the invite</span>;
    const cur = (k === 'email' ? user.email : user.mobile) ?? '';
    const val = (k === 'email' ? em : ph);
    if (val && val !== cur) return <span className="sc-idst pend">When you save, {k === 'email' ? 'this email' : 'this number'} becomes their sign-in {k === 'email' ? 'email' : 'mobile'}.</span>;
    if (cur) return <span className="sc-idst ok"><Ic k="check" />Can be used to sign in</span>;
    return null;
  };

  const profileTab = (
    <>
      <div className={`sc-dw-sec${profileDirty ? ' sc-edited' : ''}`}>
        <h5>Profile</h5>
        <div className="sc-pf-head2">
          <div className="sc-pf-left">
            <span className="sc-ring"><Avatar u={{ ...user, firstName: invite ? user.firstName : draft.firstName, lastName: invite ? user.lastName : draft.lastName }} /></span>
            <GroupPill ids={draft.groups} groups={groups} onClick={() => setTab('access')} />
          </div>
          <div className="sc-pf-grid">
            {invite ? (
              <div className="sc-pf w"><span className="sc-idst">They fill in their own name when they accept the invite.</span></div>
            ) : (
              <>
                <TextField id="ud-first" label="First name" value={draft.firstName} err={errs.firstName} disabled={!canEdit} onChange={(v) => setDraft({ ...draft, firstName: v })} />
                <TextField id="ud-last" label="Last name" value={draft.lastName} err={errs.lastName} disabled={!canEdit} onChange={(v) => setDraft({ ...draft, lastName: v })} />
              </>
            )}
            <TextField id="ud-title" label="Job title" wide value={draft.jobTitle} placeholder="e.g. Physician, Paralegal, Dispatcher" disabled={!canEdit} onChange={(v) => setDraft({ ...draft, jobTitle: v })} />
          </div>
        </div>
        <div className="sc-pf-grid" style={{ marginTop: 12 }}>
          <TextField id="ud-email" label="Email" type="email" value={draft.email} placeholder="name@example.com" err={errs.email} disabled={!canEdit} onChange={(v) => setDraft({ ...draft, email: v })} after={idStatus('email')} />
          <TextField id="ud-mobile" label="Mobile" type="tel" value={draft.mobile} placeholder="(555) 555-0100" err={errs.mobile} disabled={!canEdit} onChange={(v) => setDraft({ ...draft, mobile: v })} after={idStatus('mobile')} />
          <div className="sc-pf w">
            {errs.ident && <span className="sc-err">{errs.ident}</span>}
            <span className="sc-idst">Either one signs in, with the password. At least one is needed, and each must be unique in your organization.</span>
            {!canEdit && <span className="sc-idst">Your group can see profiles but not change them.</span>}
          </div>
        </div>
      </div>

      {!invite && (
        <div className="sc-dw-sec">
          <h5><Ic k="shieldOk" /> Sign-in and 2-step verification</h5>
          <p className="sc-sub">
            {m.length ? `Their second step: ${m.map((x) => (x === 'sms' ? 'a code texted to their mobile' : 'a code from their authenticator app')).join(' or ')}. You can’t see the codes - only reset them.`
              : requiredFor(user, policy, groups) ? `No second step yet. They must set one up by ${enforceShort(policy)}.` : 'No second step set up.'}
          </p>
          {(m.length > 0 || (user.backupLeft ?? 0) > 0) && (
            <div className="sc-list">
              {m.includes('sms') && <div className="sc-li"><span className="ic"><Ic k="sms" /></span><div className="tx"><b>Text message code{m[0] === 'sms' && <span className="sc-m-tag">Primary</span>}</b><span>Mobile ending {lastFour(user.mobile) || '—'}</span></div></div>}
              {m.includes('totp') && <div className="sc-li"><span className="ic"><Ic k="app" /></span><div className="tx"><b>Authenticator app{m[0] === 'totp' && <span className="sc-m-tag">Primary</span>}</b><span>Set up</span></div></div>}
              {(user.backupLeft ?? 0) > 0 && <div className="sc-li"><span className="ic"><Ic k="codes" /></span><div className="tx"><b>Backup codes</b><span>{user.backupLeft} of 10 left</span></div></div>}
            </div>
          )}
          <div className="sc-futline"><Ic k="lock" /><span>Passkeys and security keys</span><span className="sc-future">Future</span></div>
          <div className="sc-btnrow">
            {hasAccess('sec_users', 'pwreset') && user.status === 'active' && <button type="button" className="sc-secondary" onClick={() => actions.pwReset(user)}><Ic k="key" /> Send password reset link</button>}
            {hasAccess('sec_users', 'mfa') && m.length > 0 && <button type="button" className="sc-secondary" onClick={() => actions.reset2(user)}><Ic k="reset" /> Reset 2-step verification</button>}
          </div>
        </div>
      )}

      {user.status === 'active' && hasAccess('sec_users', 'signout') && (
        <div className="sc-dw-sec">
          <h5>Where they’re signed in</h5>
          <p className="sc-sub">Ends every session they have open, on every device. They sign in again at their next click.{user.lastLoginAt ? ` Last sign-in: ${fmtDate(user.lastLoginAt)}, ${fmtClock(user.lastLoginAt)}.` : ''}</p>
          <div className="sc-btnrow" style={{ marginTop: 0 }}><button type="button" className="sc-secondary" onClick={() => void actions.signOut(user)}><Ic k="out" /> Sign out everywhere</button></div>
        </div>
      )}
      <LongSignInBlock user={user} self={user.self} />{/* v12.10.30 (#2234): the last block of the profile, only when Support gave it */}
    </>
  );

  const danger = (() => {
    if (invite) {
      if (!hasAccess('sec_users', 'invite')) return null;
      return (
        <div className="sc-dw-sec danger"><div className="sc-dz"><div><b>Cancel invite</b><span>The link stops working. You can invite them again later.</span></div>
          <button type="button" className="sc-danger" onClick={() => actions.cancelInvite(user, onClose)}>Cancel invite</button></div></div>
      );
    }
    if (user.status === 'disabled') {
      const canDis = hasAccess('sec_users', 'disable'), canDel = hasAccess('sec_users', 'del');
      if (!canDis && !canDel) return null;
      return (
        <div className="sc-dw-sec danger">
          {canDis && <div className="sc-dz"><div><b>Enable user</b><span>They sign in with their existing password. Someone disabled for more than 90 days sets a new one at next sign-in.</span></div>
            <button type="button" className="sc-secondary" onClick={() => actions.enable(user)}><Ic k="unlock" /> Enable user</button></div>}
          {canDel && <div className="sc-dz"><div><b>Delete user</b><span>Can’t be undone. History keeps their name as “(deleted)”.</span></div>
            <button type="button" className="sc-danger" onClick={() => actions.del(user, onClose)}><Ic k="trash" /> Delete</button></div>}
        </div>
      );
    }
    if (!hasAccess('sec_users', 'disable') || user.self) return null;
    return (
      <div className="sc-dw-sec danger"><div className="sc-dz"><div><b>Disable user</b><span>Signs them out and blocks sign-in. History stays. Reversible. Delete becomes available once they’re disabled.</span></div>
        <button type="button" className="sc-danger" disabled={lastMgr} title={lastMgr ? 'The only person who can manage users and groups' : undefined} onClick={() => actions.disable(user)}><Ic k="ban" /> Disable</button></div></div>
    );
  })();

  const accessTab = (
    <>
      <div className={`sc-dw-sec${accessDirty ? ' sc-edited' : ''}`}>
        <h5>Groups{invite && <span className="sc-idst" style={{ display: 'inline', fontWeight: 500 }}>· apply when they accept</span>}</h5>
        <p className="sc-sub">What they can open and change. With several groups, each area gets the most generous level.</p>
        <GroupPicker value={draft.groups} groups={groups} locked={!canGroups || lastMgr} onChange={(ids) => setDraft({ ...draft, groups: ids })} />
        {lastMgr && <div className="sc-lock-note"><Ic k="lock" /><span>{name} is the only active person who can manage users and groups. Give that to someone else before changing these groups.</span></div>}
        {!lastMgr && !canGroups && <div className="sc-lock-note"><Ic k="lock" /><span>Your group can’t change people’s groups.</span></div>}
        {errs.groups && <span className="sc-err">{errs.groups}</span>}
      </div>
      {invite && (
        <div className="sc-dw-sec">
          <h5>Invitation</h5>
          <p className="sc-sub">
            Sent {fmtDate(user.invitedAt) || 'recently'} by {user.email ? 'email' : 'text'}{user.invitedBy ? ` by ${user.invitedBy}` : ''}{user.requestedBy ? ` - requested by ${user.requestedBy}` : ''}.{' '}
            {user.status === 'expired' ? `The link expired${user.inviteExpiresAt ? ` ${fmtDate(user.inviteExpiresAt, { month: 'short', day: 'numeric' })}` : ''} - resend to send a fresh one.` : `The link expires ${fmtDate(user.inviteExpiresAt) || 'in 7 days'}.`}{' '}
            They choose their own password when they accept.
          </p>
          {hasAccess('sec_users', 'invite') && <div className="sc-btnrow" style={{ marginTop: 0 }}><button type="button" className="sc-secondary" onClick={() => void actions.resend(user)}><Ic k="send" /> Resend invite</button>
            <button type="button" className="sc-secondary" onClick={() => void actions.copyInviteLink(user)}><Ic k="copy" /> Copy invite link</button>{/* v12.10.26 (#2133) */}</div>}
        </div>
      )}
      {danger}
    </>
  );

  return (
    <>
      {createPortal(
        <div className="vs-sec sc-drawer-scrim" onMouseDown={(e) => { if (e.target === e.currentTarget) requestClose(); }}>
          <div className="sc-drawer" role="dialog" aria-modal="true" aria-label={name}>
            <div className="dw-head">
              <div className="sc-dw-id"><div style={{ minWidth: 0 }}>
                {backTo && <button type="button" className="sc-backlink" onClick={() => { if (dirty) requestClose(); else backTo.onBack(); }}><Ic k="back" /> {backTo.label}</button>}
                <h4>{name}<StatusTags u={user} /></h4>
                <p>{[groupsLabel(user.groups, groups), user.jobTitle].filter(Boolean).join(' · ')}</p>
              </div></div>
              <button type="button" className="modal-x" onClick={requestClose} aria-label="Close"><Ic k="x" /></button>
            </div>
            <div className="sc-dw-tabs" role="tablist">
              {([['profile', 'Profile', profileDirty], ['access', 'Access', accessDirty || !!errs.groups]] as const).map(([k, l, d]) => (
                <button key={k} type="button" role="tab" className={`sc-dw-tab${tab === k ? ' on' : ''}${d ? ' sc-edited' : ''}`} aria-selected={tab === k} title={d ? 'Unsaved changes' : undefined} onClick={() => setTab(k)}><span>{l}</span></button>
              ))}
            </div>
            <div className="dw-body">
              {lockBanner}
              {tab === 'profile' ? profileTab : accessTab}
              {err && <InlineError text={err} />}
            </div>
            <div className="dw-foot">
              <button type="button" className="mc" onClick={requestClose}>{dirty ? 'Cancel' : 'Close'}</button>
              <button type="button" className="ma" disabled={!canSave || saving} onClick={() => void save()}><Ic k="check" /> {saving ? 'Saving…' : 'Save changes'}</button>
            </div>
          </div>
        </div>,
        document.body,
      )}
      {confirm && <ConfirmDialog opts={confirm} onClose={() => setConfirm(null)} />}
    </>
  );
}

function TextField({ id, label, value, onChange, err, placeholder, wide, type = 'text', disabled, after }: {
  id: string; label: string; value: string; onChange: (v: string) => void; err?: string; placeholder?: string; wide?: boolean; type?: string; disabled?: boolean; after?: ReactNode;
}) {
  return (
    <div className={`sc-pf${wide ? ' w' : ''}`}>
      <label htmlFor={id}>{label}</label>
      {/* v12.10.26 (#2135): a phone field is the app's standard phone input - mask as you type, paste cleaned */}
      <input id={id} className={`sc-in${err ? ' err' : ''}`} type={type} inputMode={type === 'tel' ? 'tel' : undefined} value={type === 'tel' ? maskPhone(value) : value}
        placeholder={type === 'tel' ? '+1 (___) ___-____' : placeholder}
        disabled={disabled} onChange={(e) => onChange(type === 'tel' ? phoneFromInput(e.target.value, value) : e.target.value)} />
      {err && <span className="sc-err">{err}</span>}
      {after}
    </div>
  );
}
